import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d71cl0jk1jkj467r5dog';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

interface QuoteFormCTAProps {
  heading?: string;
  subCopy?: string;
  formId?: string;
}

export default function QuoteFormCTA({
  heading = 'Schedule Your Painting Consultation Today',
  subCopy = "Request a free consultation and quote from Elk Grove Village's precision painting experts. No pressure — just a clear scope and honest pricing.",
  formId = 'quote-form',
}: QuoteFormCTAProps) {
  const [formState, setFormState] = useState<FormState>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    const form = e.currentTarget;
    const body = new URLSearchParams(new FormData(form) as unknown as Record<string, string>).toString();
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) { setFormState('success'); form.reset(); }
      else setFormState('error');
    } catch { setFormState('error'); }
  };

  return (
    <section id="quote" className="relative py-28 overflow-hidden" style={{ background: '#0f0f0f' }}>
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/2c439c8b6dcbfa9362b674975fe0e8b8.jpg"
          alt="Premium painting Elk Grove Village Illinois"
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-charcoal-900/70" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — copy */}
          <div>
            <span className="font-sans text-gold text-xs tracking-[0.3em] uppercase">Ready to Begin?</span>
            <h2 className="font-serif text-warm-white text-4xl md:text-5xl mt-4 mb-5 leading-tight">
              {heading.includes('\n')
                ? heading.split('\n').map((line, i) => (
                    <span key={i}>{line}{i < heading.split('\n').length - 1 && <br />}</span>
                  ))
                : <><span>{heading.split(' ').slice(0, -2).join(' ')}</span><br /><em className="text-gold not-italic">{heading.split(' ').slice(-2).join(' ')}</em></>
              }
            </h2>
            <p className="font-sans text-warm-muted text-base leading-relaxed mb-8 max-w-md">{subCopy}</p>
            <div className="flex flex-col gap-5">
              <a href="tel:8478701600" className="inline-flex items-center gap-3 text-warm-white hover:text-gold transition-colors duration-200 cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full border border-gold/30 shrink-0">
                  <i className="ri-phone-fill text-gold" style={{ fontSize: '18px' }} />
                </div>
                <div>
                  <span className="font-sans text-warm-dim text-xs block">Call us directly</span>
                  <span className="font-serif text-warm-white text-xl">(847) 870-1600</span>
                </div>
              </a>
              <a href="mailto:sales@hometownpainters.com" className="inline-flex items-center gap-3 text-warm-white hover:text-gold transition-colors duration-200 cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full border border-gold/30 shrink-0">
                  <i className="ri-mail-line text-gold" style={{ fontSize: '18px' }} />
                </div>
                <div>
                  <span className="font-sans text-warm-dim text-xs block">Email us</span>
                  <span className="font-sans text-warm-white text-base">sales@hometownpainters.com</span>
                </div>
              </a>
              <div className="flex items-center gap-3 text-warm-white">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full border border-gold/30 shrink-0">
                  <i className="ri-map-pin-2-line text-gold" style={{ fontSize: '18px' }} />
                </div>
                <div>
                  <span className="font-sans text-warm-dim text-xs block">Our address</span>
                  <span className="font-sans text-warm-white text-sm">1451 Landmeier Rd Unit G-B, Elk Grove Village, IL 60007</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-charcoal-800 border border-gold/15 rounded-2xl p-8">
            {formState === 'success' ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 flex items-center justify-center bg-gold/20 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-line text-gold" style={{ fontSize: '28px' }} />
                </div>
                <h3 className="font-serif text-warm-white text-2xl mb-3">Request Received!</h3>
                <p className="font-sans text-warm-muted text-sm">Thank you! We&apos;ll be in touch within one business day to schedule your consultation.</p>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-warm-white text-xl mb-1">Request Your Free Quote</h3>
                <p className="font-sans text-warm-dim text-xs mb-6">Free consultation · No obligation · Elk Grove Village &amp; surrounding IL areas</p>
                <form data-readdy-form id={formId} onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-sans text-warm-dim text-xs block mb-1.5">First Name</label>
                      <input type="text" name="first_name" required placeholder="John" className="w-full bg-charcoal-900 border border-gold/10 rounded-lg px-4 py-3 text-warm-white font-sans text-sm placeholder:text-warm-dim/40 focus:outline-none focus:border-gold/40 transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="font-sans text-warm-dim text-xs block mb-1.5">Last Name</label>
                      <input type="text" name="last_name" required placeholder="Doe" className="w-full bg-charcoal-900 border border-gold/10 rounded-lg px-4 py-3 text-warm-white font-sans text-sm placeholder:text-warm-dim/40 focus:outline-none focus:border-gold/40 transition-colors duration-200" />
                    </div>
                  </div>
                  <div>
                    <label className="font-sans text-warm-dim text-xs block mb-1.5">Email Address</label>
                    <input type="email" name="email" required placeholder="john@example.com" className="w-full bg-charcoal-900 border border-gold/10 rounded-lg px-4 py-3 text-warm-white font-sans text-sm placeholder:text-warm-dim/40 focus:outline-none focus:border-gold/40 transition-colors duration-200" />
                  </div>
                  <div>
                    <label className="font-sans text-warm-dim text-xs block mb-1.5">Phone Number</label>
                    <input type="tel" name="phone" required placeholder="(847) 000-0000" className="w-full bg-charcoal-900 border border-gold/10 rounded-lg px-4 py-3 text-warm-white font-sans text-sm placeholder:text-warm-dim/40 focus:outline-none focus:border-gold/40 transition-colors duration-200" />
                  </div>
                  <div>
                    <label className="font-sans text-warm-dim text-xs block mb-1.5">Service Needed</label>
                    <select name="service" required className="w-full bg-charcoal-900 border border-gold/10 rounded-lg px-4 py-3 text-warm-white font-sans text-sm focus:outline-none focus:border-gold/40 transition-colors duration-200 cursor-pointer">
                      <option value="">Select a service...</option>
                      <option value="interior">Interior Painting</option>
                      <option value="exterior">Exterior Painting</option>
                      <option value="cabinet">Cabinet Painting</option>
                      <option value="commercial">Commercial Painting</option>
                      <option value="hoa">HOA Painting</option>
                      <option value="new-construction">New Construction</option>
                      <option value="other">Other / Multiple</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-sans text-warm-dim text-xs block mb-1.5">Project Details</label>
                    <textarea name="message" rows={3} maxLength={500} placeholder="Briefly describe your project..." className="w-full bg-charcoal-900 border border-gold/10 rounded-lg px-4 py-3 text-warm-white font-sans text-sm placeholder:text-warm-dim/40 focus:outline-none focus:border-gold/40 transition-colors duration-200 resize-none" />
                  </div>
                  <button type="submit" disabled={formState === 'submitting'} className="w-full bg-gold text-charcoal-900 font-sans font-medium text-sm py-4 rounded-full hover:bg-gold-light transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                    {formState === 'submitting' ? 'Sending...' : 'Request Your Free Quote'}
                  </button>
                  {formState === 'error' && (
                    <p className="font-sans text-red-400 text-xs text-center">Something went wrong. Please try again or call us at (847) 870-1600.</p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
